/**
 * SearchPreviewDrawer — Right context drawer for article preview
 * 
 * Shows:
 * - Title and metadata
 * - Abstract preview
 * - AI summary if available
 * - Save button
 * - Open source/PubMed link
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../ui/sheet';
import { ScrollArea } from '../../ui/scroll-area';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import { SearchArticle } from './SearchResultRow';
import {
  BookmarkPlus,
  BookmarkCheck,
  ExternalLink,
  GraduationCap,
  Loader2,
  Calendar,
  Building,
  Users,
  X,
} from 'lucide-react';

interface SearchPreviewDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  article: SearchArticle | null;
  isSaved: boolean;
  isSaving: boolean;
  onSave: () => void;
  onOpenInLearn: () => void;
}

export const SearchPreviewDrawer: React.FC<SearchPreviewDrawerProps> = ({
  open,
  onOpenChange,
  article,
  isSaved,
  isSaving,
  onSave,
  onOpenInLearn,
}) => {
  if (!article) return null;

  const formattedDate = article.pub_date
    ? new Date(article.pub_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:w-[420px] lg:w-[480px] p-0 flex flex-col"
        data-testid="search-preview-drawer"
      >
        {/* Header */}
        <SheetHeader className="p-4 border-b border-[hsl(var(--border))] flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="responsive-title text-base font-semibold leading-snug line-clamp-3">
                {article.title}
              </SheetTitle>
              <SheetDescription className="mt-2 space-y-1">
                {article.journal && (
                  <div className="flex items-center gap-1.5 text-sm text-[hsl(var(--primary))]">
                    <Building className="w-3.5 h-3.5" />
                    <span>{article.journal}</span>
                  </div>
                )}
                {formattedDate && (
                  <div className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))]">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{formattedDate}</span>
                  </div>
                )}
              </SheetDescription>
            </div>
          </div>

          {/* Study design tags */}
          {article.design_tags && article.design_tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {article.design_tags.map((tag, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 mt-4">
            {isSaved ? (
              <Button
                variant="outline"
                className="flex-1 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
                onClick={onOpenInLearn}
                data-testid="preview-open-in-learn"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Open in Learn
              </Button>
            ) : (
              <Button
                className="flex-1"
                onClick={onSave}
                disabled={isSaving}
                data-testid="preview-save-button"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <BookmarkPlus className="w-4 h-4 mr-2" />
                )}
                Save to LitHub
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => window.open(`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`, '_blank')}
              data-testid="preview-open-pubmed"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              PubMed
            </Button>
          </div>
        </SheetHeader>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* AI Summary */}
            {article.ai_summary && (
              <div>
                <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                  AI Summary
                </h4>
                <div className="bg-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/20 rounded-lg p-3">
                  <p className="text-sm text-[hsl(var(--foreground))] leading-relaxed">
                    {article.ai_summary}
                  </p>
                </div>
              </div>
            )}

            {/* Abstract */}
            {article.abstract && (
              <div>
                <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                  Abstract
                </h4>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                  {article.abstract}
                </p>
              </div>
            )}

            {/* Authors */}
            {article.authors && (
              <div>
                <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                  Authors
                </h4>
                <div className="flex items-start gap-1.5">
                  <Users className="w-4 h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {article.authors}
                  </p>
                </div>
              </div>
            )}

            {/* PMID */}
            <div>
              <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                PubMed ID
              </h4>
              <code className="text-sm text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-1 rounded">
                {article.pmid}
              </code>
            </div>
          </div>
        </ScrollArea>

        {/* Footer with save confirmation */}
        {isSaved && (
          <div className="p-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--success))]/5 flex-shrink-0">
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--success))]">
              <BookmarkCheck className="w-4 h-4" />
              <span>Saved to LitHub Inbox</span>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto h-7 text-xs"
                onClick={onOpenInLearn}
              >
                Open in Learn →
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SearchPreviewDrawer;
